import React, { useId } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'

interface ConfirmDialogProps {
    open: boolean
    title: string
    body: React.ReactNode
    cancelLabel: string
    confirmLabel: string
    onClose: () => void
    onConfirm: () => void
    confirmColor?: 'primary' | 'warning' | 'error'
    // Disables both buttons instead of closing on click, for actions that stay open until an
    // awaited result comes back (e.g. logout) rather than closing immediately and firing in the background.
    pending?: boolean
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title,
    body,
    cancelLabel,
    confirmLabel,
    onClose,
    onConfirm,
    confirmColor = 'primary',
    pending = false,
}) => {
    // DialogTitle auto-wires aria-labelledby; aria-describedby needs doing by hand.
    const descriptionId = useId()

    return (
        <Dialog
            open={open}
            // Disabling the buttons isn't enough on its own — Dialog still calls onClose for a
            // backdrop click or Escape regardless, which would dismiss a pending (e.g. logging
            // out) dialog while its request is still in flight.
            onClose={pending ? undefined : onClose}
            disableEscapeKeyDown={pending}
            maxWidth="xs"
            fullWidth
            aria-describedby={descriptionId}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {/* component="div", not the default <p> — some callers pass block-level content
                    (e.g. an avatar + name block) as the body, which <p> can't legally contain. */}
                <Typography
                    id={descriptionId}
                    component="div"
                    sx={{ fontSize: 14.5, color: 'text.secondary', lineHeight: 1.5 }}
                >
                    {body}
                </Typography>
            </DialogContent>
            <DialogActions>
                {/* eslint-disable-next-line jsx-a11y/no-autofocus -- so Enter never confirms the risky action by accident */}
                <Button onClick={onClose} disabled={pending} autoFocus>
                    {cancelLabel}
                </Button>
                <Button variant="contained" color={confirmColor} onClick={onConfirm} disabled={pending}>
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
